import * as React from "react"
import { AnimatePresence, motion } from "motion/react"
import {
  ArrowLeft,
  ArrowRight,
  Check,
  CheckCircle2,
  ChevronRight,
  CreditCard,
  Loader2,
  Minus,
  Package,
  Plus,
  RefreshCw,
  ShieldCheck,
  ShoppingBag,
  Star,
  Trash2,
  X,
} from "lucide-react"
import { useAuth, type StoreOrder, type StoreProduct } from "@/contexts/AuthContext"

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08, delayChildren: 0.05 },
  },
}

const itemVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { type: "spring" as const, bounce: 0.3, duration: 0.45 },
  },
}

type CartMap = Record<string, number>
type CheckoutStep = "cart" | "shipping" | "review" | "success"

type ShippingForm = {
  fullName: string
  email: string
  phone: string
  addressLine1: string
  addressLine2: string
  city: string
  state: string
  postalCode: string
  country: string
  notes: string
}

type SavedShippingProfile = Omit<ShippingForm, "notes">

const SAVED_SHIPPING_KEY = "oval_store_saved_shipping"

const currency = (value: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(value)

const formatOrderDate = (iso: string) =>
  new Date(iso).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  })

function getDisplayStockLabel(product: StoreProduct): string {
  if (!product.inStock || product.stock <= 0) return "Out of stock"
  if (product.stock <= 5) return `Only ${product.stock} left`
  return `${product.stock} in stock`
}

function getInitialShipping(user: any): ShippingForm {
  return {
    fullName:
      user?.onboardingData?.displayName ||
      user?.onboardingData?.agencyName ||
      user?.email?.split("@")[0] ||
      "",
    email: user?.email || "",
    phone: "",
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    postalCode: "",
    country: "United States",
    notes: "",
  }
}

function ProductTileImage({ product }: { product: StoreProduct }) {
  if (product.primaryImageUrl) {
    return (
      <img
        src={product.primaryImageUrl}
        alt={product.name}
        className="h-44 w-full rounded-xl object-cover border-3 border-[#2A2A45]"
      />
    )
  }

  return (
    <div className="h-44 w-full rounded-xl bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 border-3 border-[#2A2A45] flex items-center justify-center">
      <div className="text-center text-[#6A6A8A]">
        <Package className="w-7 h-7 mx-auto" />
        <p className="mt-2 text-xs font-bold" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>Image pending</p>
      </div>
    </div>
  )
}

function QtyControl({
  value,
  onDecrease,
  onIncrease,
  canIncrease,
  compact,
}: {
  value: number
  onDecrease: () => void
  onIncrease: () => void
  canIncrease: boolean
  compact?: boolean
}) {
  return (
    <div
      className={[
        "inline-flex items-center rounded-xl border-3 border-[#2A2A45] bg-[#1C1C32] shadow-[0_0_6px_rgba(243,112,32,0.1)]",
        compact ? "h-9" : "h-10",
      ].join(" ")}
    >
      <button
        type="button"
        onClick={onDecrease}
        disabled={value <= 0}
        className="w-9 h-full inline-flex items-center justify-center text-[#CCCCCC] disabled:text-[#6A6A8A] disabled:cursor-not-allowed hover:bg-[#1A1A30] rounded-l-lg transition-colors font-bold"
      >
        <Minus className="w-4 h-4" />
      </button>
      <div
        className="min-w-[38px] px-1 text-center text-sm font-black text-white"
        style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
      >
        {value}
      </div>
      <button
        type="button"
        onClick={onIncrease}
        disabled={!canIncrease}
        className="w-9 h-full inline-flex items-center justify-center text-[#CCCCCC] disabled:text-[#6A6A8A] disabled:cursor-not-allowed hover:bg-[#1A1A30] rounded-r-lg transition-colors font-bold"
      >
        <Plus className="w-4 h-4" />
      </button>
    </div>
  )
}

function CheckoutStepPill({
  label,
  active,
  done,
}: {
  label: string
  active: boolean
  done: boolean
}) {
  return (
    <div
      className={[
        "inline-flex items-center gap-2 rounded-xl px-3 py-2 text-xs font-bold border-3 border-[#2A2A45] transition-colors",
        active
          ? "bg-[#F37020] text-white shadow-[0_0_8px_rgba(243,112,32,0.12)]"
          : done
            ? "bg-[#32CD32] text-white shadow-[0_0_6px_rgba(243,112,32,0.1)]"
            : "bg-[#1C1C32] text-[#6A6A8A] shadow-[0_0_6px_rgba(243,112,32,0.1)]",
      ].join(" ")}
      style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
    >
      <span
        className={[
          "w-5 h-5 rounded-full inline-flex items-center justify-center border-2 border-[#2A2A45]",
          active ? "bg-[#1C1C32]/50" : done ? "bg-[#1C1C32]/50" : "bg-[#1A1A30]",
        ].join(" ")}
      >
        {done ? <Check className="w-3 h-3" /> : <span className="w-1.5 h-1.5 rounded-full bg-current" />}
      </span>
      {label}
    </div>
  )
}

function Field({
  label,
  value,
  onChange,
  placeholder,
  type,
  optional,
}: {
  label: string
  value: string
  onChange: (value: string) => void
  placeholder?: string
  type?: string
  optional?: boolean
}) {
  return (
    <label className="block">
      <div className="mb-1.5 flex items-center gap-2 text-xs font-bold text-[#9A9ABF]" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>
        <span>{label}</span>
        {optional ? <span className="text-[#6A6A8A] font-medium">Optional</span> : null}
      </div>
      <input
        type={type || "text"}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full h-11 rounded-xl border-3 border-[#2A2A45] bg-[#1C1C32] px-3.5 text-sm text-white font-medium outline-none focus:border-[#F37020] focus:ring-4 focus:ring-[#F37020]/20 shadow-[0_0_6px_rgba(243,112,32,0.1)] transition-colors"
        style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
      />
    </label>
  )
}

function TextAreaField({
  label,
  value,
  onChange,
  placeholder,
  optional,
}: {
  label: string
  value: string
  onChange: (value: string) => void
  placeholder?: string
  optional?: boolean
}) {
  return (
    <label className="block">
      <div className="mb-1.5 flex items-center gap-2 text-xs font-bold text-[#9A9ABF]" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>
        <span>{label}</span>
        {optional ? <span className="text-[#6A6A8A] font-medium">Optional</span> : null}
      </div>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={3}
        placeholder={placeholder}
        className="w-full rounded-xl border-3 border-[#2A2A45] bg-[#1C1C32] px-3.5 py-2.5 text-sm text-white font-medium outline-none focus:border-[#F37020] focus:ring-4 focus:ring-[#F37020]/20 shadow-[0_0_6px_rgba(243,112,32,0.1)] resize-y transition-colors"
        style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
      />
    </label>
  )
}

export function StoreTab() {
  const { user, getStoreProducts, getMyStoreOrders, mockStoreCheckout } = useAuth()

  const [products, setProducts] = React.useState<StoreProduct[]>([])
  const [orders, setOrders] = React.useState<StoreOrder[]>([])
  const [loading, setLoading] = React.useState(true)
  const [refreshing, setRefreshing] = React.useState(false)
  const [submitting, setSubmitting] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [cart, setCart] = React.useState<CartMap>({})
  const [latestOrder, setLatestOrder] = React.useState<StoreOrder | null>(null)

  const [checkoutOpen, setCheckoutOpen] = React.useState(false)
  const [checkoutStep, setCheckoutStep] = React.useState<CheckoutStep>("cart")
  const [checkoutMessage, setCheckoutMessage] = React.useState<{ type: "success" | "error"; text: string } | null>(null)

  const [shipping, setShipping] = React.useState<ShippingForm>(() => getInitialShipping(user))
  const [savedShipping, setSavedShipping] = React.useState<SavedShippingProfile | null>(null)
  const [saveShippingForNextTime, setSaveShippingForNextTime] = React.useState(true)

  const loadStore = React.useCallback(async (silent?: boolean) => {
    if (!silent) setLoading(true)
    setError(null)

    const [productsRes, ordersRes] = await Promise.all([getStoreProducts(), getMyStoreOrders()])
    let nextError: string | null = null

    if (productsRes.success && productsRes.products) {
      setProducts(productsRes.products)
      setCart((prev) => {
        const next: CartMap = {}
        for (const product of productsRes.products!) {
          const qty = prev[product._id] || 0
          const maxAllowed = Math.min(product.stock, product.maxPerOrder ?? product.stock)
          if (qty > 0 && maxAllowed > 0) {
            next[product._id] = Math.min(qty, maxAllowed)
          }
        }
        return next
      })
    } else {
      nextError = productsRes.message || "Failed to load store products"
    }

    if (ordersRes.success && ordersRes.orders) {
      setOrders(ordersRes.orders)
    } else if (!nextError) {
      nextError = ordersRes.message || "Failed to load recent orders"
    }

    setError(nextError)
    if (!silent) setLoading(false)
  }, [getStoreProducts, getMyStoreOrders])

  React.useEffect(() => {
    loadStore()
  }, [loadStore])

  React.useEffect(() => {
    try {
      const raw = localStorage.getItem(SAVED_SHIPPING_KEY)
      if (!raw) return
      const parsed = JSON.parse(raw)
      if (parsed && typeof parsed === "object") {
        setSavedShipping({
          fullName: String(parsed.fullName || ""),
          email: String(parsed.email || ""),
          phone: String(parsed.phone || ""),
          addressLine1: String(parsed.addressLine1 || ""),
          addressLine2: String(parsed.addressLine2 || ""),
          city: String(parsed.city || ""),
          state: String(parsed.state || ""),
          postalCode: String(parsed.postalCode || ""),
          country: String(parsed.country || "United States"),
        })
      }
    } catch {
      localStorage.removeItem(SAVED_SHIPPING_KEY)
    }
  }, [])

  React.useEffect(() => {
    setShipping((prev) => {
      if (prev.email.trim()) return prev
      return { ...prev, email: user?.email || prev.email }
    })
  }, [user?.email])

  const refreshStore = async () => {
    setRefreshing(true)
    await loadStore(true)
    setRefreshing(false)
  }

  const productMap = React.useMemo(() => new Map(products.map((p) => [p._id, p])), [products])

  const cartItems = React.useMemo(() => {
    return Object.entries(cart)
      .map(([productId, quantity]) => {
        const product = productMap.get(productId)
        if (!product || quantity <= 0) return null
        return { product, quantity }
      })
      .filter(Boolean) as Array<{ product: StoreProduct; quantity: number }>
  }, [cart, productMap])

  const cartCount = React.useMemo(
    () => cartItems.reduce((sum, item) => sum + item.quantity, 0),
    [cartItems]
  )
  const cartSubtotal = React.useMemo(
    () => cartItems.reduce((sum, item) => sum + item.product.priceUsd * item.quantity, 0),
    [cartItems]
  )

  const setCartQuantity = React.useCallback((product: StoreProduct, nextQuantity: number) => {
    const maxAllowed = Math.max(0, Math.min(product.stock, product.maxPerOrder ?? product.stock))
    const clamped = Math.max(0, Math.min(nextQuantity, maxAllowed))

    setCart((prev) => {
      if (clamped <= 0) {
        const next = { ...prev }
        delete next[product._id]
        return next
      }
      return { ...prev, [product._id]: clamped }
    })
  }, [])

  const updateShippingField = <K extends keyof ShippingForm>(key: K, value: ShippingForm[K]) => {
    setShipping((prev) => ({ ...prev, [key]: value }))
  }

  const shippingValid = React.useMemo(() => {
    return Boolean(
      shipping.fullName.trim() &&
        shipping.email.trim() &&
        shipping.addressLine1.trim() &&
        shipping.city.trim() &&
        shipping.state.trim() &&
        shipping.postalCode.trim() &&
        shipping.country.trim()
    )
  }, [shipping])

  const openCheckout = () => {
    if (cartItems.length === 0) return
    setCheckoutStep("cart")
    setCheckoutMessage(null)
    setCheckoutOpen(true)
  }

  const closeCheckout = () => {
    if (submitting) return
    setCheckoutOpen(false)
    setCheckoutMessage(null)
    if (checkoutStep === "success") {
      setCheckoutStep("cart")
    }
  }

  const goNextStep = () => {
    setCheckoutMessage(null)
    if (checkoutStep === "cart") {
      if (cartItems.length === 0) {
        setCheckoutMessage({ type: "error", text: "Your bag is empty." })
        return
      }
      setCheckoutStep("shipping")
      return
    }

    if (checkoutStep === "shipping") {
      if (!shippingValid) {
        setCheckoutMessage({ type: "error", text: "Please complete the required shipping fields." })
        return
      }
      setCheckoutStep("review")
    }
  }

  const goPrevStep = () => {
    setCheckoutMessage(null)
    if (checkoutStep === "review") {
      setCheckoutStep("shipping")
      return
    }
    if (checkoutStep === "shipping") {
      setCheckoutStep("cart")
    }
  }

  const applySavedShipping = () => {
    if (!savedShipping) return
    setShipping((prev) => ({ ...prev, ...savedShipping }))
    setCheckoutMessage({ type: "success", text: "Saved shipping details applied." })
  }

  const handleCheckout = async () => {
    if (cartItems.length === 0) {
      setCheckoutMessage({ type: "error", text: "Your bag is empty." })
      setCheckoutStep("cart")
      return
    }
    if (!shippingValid) {
      setCheckoutMessage({ type: "error", text: "Please complete the required shipping fields." })
      setCheckoutStep("shipping")
      return
    }

    setSubmitting(true)
    setCheckoutMessage(null)

    const result = await mockStoreCheckout({
      items: cartItems.map((item) => ({ productId: item.product._id, quantity: item.quantity })),
      shipping: {
        fullName: shipping.fullName.trim(),
        email: shipping.email.trim(),
        phone: shipping.phone.trim() || undefined,
        addressLine1: shipping.addressLine1.trim(),
        addressLine2: shipping.addressLine2.trim() || undefined,
        city: shipping.city.trim(),
        state: shipping.state.trim(),
        postalCode: shipping.postalCode.trim(),
        country: shipping.country.trim(),
      },
      notes: shipping.notes.trim() || undefined,
    })

    if (result.success && result.order) {
      if (saveShippingForNextTime) {
        const profile: SavedShippingProfile = {
          fullName: shipping.fullName.trim(),
          email: shipping.email.trim(),
          phone: shipping.phone.trim(),
          addressLine1: shipping.addressLine1.trim(),
          addressLine2: shipping.addressLine2.trim(),
          city: shipping.city.trim(),
          state: shipping.state.trim(),
          postalCode: shipping.postalCode.trim(),
          country: shipping.country.trim(),
        }
        setSavedShipping(profile)
        localStorage.setItem(SAVED_SHIPPING_KEY, JSON.stringify(profile))
      }

      setLatestOrder(result.order)
      setOrders((prev) => [result.order!, ...prev.filter((o) => o._id !== result.order!._id)])
      setCart({})
      setShipping((prev) => ({ ...prev, notes: "" }))
      setCheckoutMessage({ type: "success", text: result.message || "Order placed." })
      setCheckoutStep("success")
      await loadStore(true)
    } else {
      setCheckoutMessage({ type: "error", text: result.message || "Checkout failed." })
    }

    setSubmitting(false)
  }

  const stepIndex = checkoutStep === "cart" ? 0 : checkoutStep === "shipping" ? 1 : checkoutStep === "review" ? 2 : 3

  return (
    <motion.div
      className="p-4 md:p-6 lg:p-8 max-w-5xl mx-auto"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Hero Header */}
      <motion.div className="mb-8" variants={itemVariants}>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-center gap-3 mb-3">
            <motion.div
              className="w-12 h-12 rounded-xl bg-[#F37020] border-3 border-[#2A2A45] flex items-center justify-center shadow-[0_0_10px_rgba(243,112,32,0.15)]"
              animate={{ rotate: [0, 6, -6, 0] }}
              transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
            >
              <ShoppingBag className="w-6 h-6 text-white" />
            </motion.div>
            <div>
              <h1 className="text-3xl md:text-4xl font-black text-white" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>
                Rewards Store
              </h1>
              <p className="text-[#9A9ABF] font-medium" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>
                Shop physical rewards with a simple checkout flow.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <motion.button
              type="button"
              onClick={refreshStore}
              disabled={refreshing}
              whileHover={{ y: -2, boxShadow: "5px_5px_0_0_#1a1a1a" }}
              whileTap={{ scale: 0.97, boxShadow: "2px_2px_0_0_#1a1a1a" }}
              className="inline-flex items-center gap-2 rounded-xl border-3 border-[#2A2A45] bg-[#1C1C32] px-4 py-2.5 text-sm font-bold text-[#CCCCCC] shadow-[0_0_10px_rgba(243,112,32,0.15)] disabled:opacity-60 transition-colors"
              style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
            >
              {refreshing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              Refresh
            </motion.button>
            <motion.button
              type="button"
              onClick={openCheckout}
              disabled={cartItems.length === 0}
              whileHover={{ y: -2, boxShadow: "5px_5px_0_0_#1a1a1a" }}
              whileTap={{ scale: 0.97, boxShadow: "2px_2px_0_0_#1a1a1a" }}
              className="inline-flex items-center gap-2 rounded-xl border-3 border-[#2A2A45] bg-[#F37020] px-4 py-2.5 text-sm font-bold text-white shadow-[0_0_10px_rgba(243,112,32,0.15)] hover:bg-[#E06010] disabled:opacity-50 transition-colors"
              style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
            >
              <ShoppingBag className="w-4 h-4" />
              Checkout ({cartCount})
            </motion.button>
          </div>
        </div>
      </motion.div>

      {/* Stats Cards */}
      <motion.div variants={itemVariants} className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {[
          { label: "Products", value: String(products.length), color: "#3B82F6" },
          { label: "In Bag", value: String(cartCount), color: "#F37020" },
          { label: "Subtotal", value: currency(cartSubtotal), color: "#32CD32" },
          { label: "Checkout", value: "Mock Payment", color: "#9B59B6", icon: ShieldCheck },
        ].map((stat) => (
          <motion.div
            key={stat.label}
            className="bg-[#1C1C32] border-4 border-[#2A2A45] rounded-2xl p-4 shadow-[0_0_10px_rgba(243,112,32,0.15)]"
            whileHover={{ y: -2, scale: 1.01 }}
          >
            <p className="text-xs font-bold text-[#6A6A8A]" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>{stat.label}</p>
            <div className="mt-1 flex items-center gap-1.5">
              {stat.icon ? <stat.icon className="w-4 h-4" style={{ color: stat.color }} /> : null}
              <p
                className="text-lg font-black text-white"
                style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif", color: stat.icon ? stat.color : undefined }}
              >
                {stat.value}
              </p>
            </div>
          </motion.div>
        ))}
      </motion.div>

      {/* Product Catalog */}
      <motion.section variants={itemVariants}>
        <div className="bg-[#1C1C32] border-4 border-[#2A2A45] rounded-2xl p-4 md:p-6 shadow-[6px_6px_0_0_#1a1a1a]">
          <div className="flex items-center justify-between gap-3 mb-4">
            <div>
              <h2
                className="text-xl md:text-2xl font-black text-white"
                style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
              >
                Catalog
              </h2>
              <p className="text-sm text-[#9A9ABF] font-medium" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>
                Tap + to build your bag. Checkout is handled in a separate modal flow.
              </p>
            </div>
            {error ? (
              <div className="rounded-xl border-3 border-[#2A2A45] bg-red-100 px-3 py-1.5 text-xs font-bold text-red-700 shadow-[0_0_6px_rgba(243,112,32,0.1)]" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>
                {error}
              </div>
            ) : null}
          </div>

          {loading ? (
            <div className="h-56 grid place-items-center text-[#6A6A8A]">
              <div className="inline-flex items-center gap-2 text-sm font-bold" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>
                <Loader2 className="w-5 h-5 animate-spin text-[#F37020]" />
                Loading store...
              </div>
            </div>
          ) : products.length === 0 ? (
            <div className="h-56 grid place-items-center text-center">
              <div>
                <Package className="w-10 h-10 mx-auto text-[#6A6A8A]" />
                <p className="mt-2 text-sm font-bold text-[#CCCCCC]" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>No products published yet</p>
                <p className="text-xs text-[#6A6A8A] mt-1 font-medium" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>Ask an admin to add store products in the admin Store tab.</p>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {products.map((product) => {
                const quantity = cart[product._id] || 0
                const maxAllowed = Math.max(0, Math.min(product.stock, product.maxPerOrder ?? product.stock))
                const canIncrease = product.inStock && quantity < maxAllowed

                return (
                  <motion.div
                    key={product._id}
                    whileHover={{ y: -3, scale: 1.01, boxShadow: "8px 8px 0 0 #1a1a1a" }}
                    className="group bg-[#1C1C32] border-4 border-[#2A2A45] rounded-2xl p-3 shadow-[6px_6px_0_0_#1a1a1a]"
                  >
                    <div className="relative overflow-hidden rounded-xl">
                      <ProductTileImage product={product} />
                      {product.featured && (
                        <div className="absolute top-2 right-2">
                          <div className="inline-flex items-center gap-1 rounded-lg bg-[#FFD700] text-black border-2 border-[#2A2A45] px-2 py-1 text-[11px] font-bold shadow-none" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>
                            <Star className="w-3 h-3" fill="currentColor" /> Featured
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="mt-3 px-1">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="text-[11px] font-bold text-[#6A6A8A]" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>{product.category}</p>
                          <h3 className="text-sm font-black text-white leading-tight" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>{product.name}</h3>
                          <p className="text-base font-black text-white mt-0.5" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>{currency(product.priceUsd)}</p>
                          <p className={`mt-0.5 text-xs font-bold ${product.inStock ? "text-[#32CD32]" : "text-red-600"}`} style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>
                            {getDisplayStockLabel(product)}
                          </p>
                        </div>
                        {product.badge ? (
                          <span className="rounded-lg border-2 border-[#2A2A45] bg-[#9B59B6] px-2 py-1 text-[10px] font-bold text-white shadow-none" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>
                            {product.badge}
                          </span>
                        ) : null}
                      </div>

                      <p className="mt-2 text-xs text-[#9A9ABF] min-h-[34px] line-clamp-2 font-medium" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>
                        {product.description || "No description yet."}
                      </p>

                      <div className="mt-3 flex items-center justify-between gap-2">
                        <QtyControl
                          value={quantity}
                          onDecrease={() => setCartQuantity(product, quantity - 1)}
                          onIncrease={() => setCartQuantity(product, quantity + 1)}
                          canIncrease={canIncrease}
                        />
                        <motion.button
                          type="button"
                          onClick={openCheckout}
                          disabled={quantity <= 0}
                          whileHover={{ scale: 1.03 }}
                          whileTap={{ scale: 0.97 }}
                          className="inline-flex items-center gap-1.5 rounded-xl border-3 border-[#2A2A45] bg-[#1C1C32] px-3 py-2 text-xs font-bold text-[#CCCCCC] shadow-[0_0_6px_rgba(243,112,32,0.1)] hover:bg-[#1A1A30] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
                        >
                          Review Bag
                          <ChevronRight className="w-3.5 h-3.5" />
                        </motion.button>
                      </div>

                      {product.maxPerOrder ? (
                        <p className="mt-2 text-[11px] text-[#6A6A8A] font-bold" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>Limit {product.maxPerOrder} per order</p>
                      ) : null}
                    </div>
                  </motion.div>
                )
              })}
            </div>
          )}
        </div>
      </motion.section>

      {/* Recent Orders */}
      <motion.section variants={itemVariants} className="mt-6">
        <div className="bg-[#1C1C32] border-4 border-[#2A2A45] rounded-2xl p-4 md:p-6 shadow-[6px_6px_0_0_#1a1a1a]">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-lg bg-[#32CD32] border-2 border-[#2A2A45] flex items-center justify-center shadow-[0_0_6px_rgba(243,112,32,0.1)]">
              <CheckCircle2 className="w-4 h-4 text-white" />
            </div>
            <h2 className="text-xl font-black text-white" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>
              Recent Orders
            </h2>
          </div>
          <p className="text-sm text-[#9A9ABF] mb-4 font-medium" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>Mock orders you've placed while testing the physical store experience.</p>

          {latestOrder ? (
            <motion.div
              className="mb-4 rounded-2xl border-4 border-[#2A2A45] bg-[#32CD32]/10 p-3.5 shadow-[0_0_10px_rgba(243,112,32,0.15)]"
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
            >
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <p className="text-sm font-black text-white" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>Latest order: {latestOrder.orderNumber}</p>
                  <p className="text-xs text-[#9A9ABF] mt-0.5 font-medium" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>{currency(latestOrder.totalUsd)} • {latestOrder.items.length} items • {formatOrderDate(latestOrder.createdAt)}</p>
                </div>
                <span className="rounded-lg border-2 border-[#2A2A45] bg-[#32CD32] px-2.5 py-1 text-[11px] font-bold text-white shadow-[0_0_6px_rgba(243,112,32,0.1)]" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>
                  Mock Paid
                </span>
              </div>
            </motion.div>
          ) : null}

          {orders.length === 0 ? (
            <div className="rounded-2xl border-3 border-dashed border-[#2A2A45] p-8 text-center">
              <ShoppingBag className="w-8 h-8 mx-auto text-[#6A6A8A]" />
              <p className="mt-2 text-sm font-bold text-[#CCCCCC]" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>No orders yet</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {orders.slice(0, 6).map((order) => (
                <motion.div
                  key={order._id}
                  className="rounded-2xl border-3 border-[#2A2A45] bg-[#1C1C32] p-3 shadow-[0_0_8px_rgba(243,112,32,0.12)]"
                  whileHover={{ y: -2, boxShadow: "5px 5px 0 0 #1a1a1a" }}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="text-sm font-black text-white" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>{order.orderNumber}</p>
                      <p className="text-xs text-[#6A6A8A] font-medium" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>{formatOrderDate(order.createdAt)}</p>
                    </div>
                    <p className="text-sm font-black text-white" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>{currency(order.totalUsd)}</p>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {order.items.map((item, index) => (
                      <span
                        key={`${order._id}-${index}`}
                        className="rounded-lg border-2 border-[#2A2A45] bg-[#1A1A30] px-2 py-1 text-[11px] font-bold text-[#CCCCCC] shadow-none"
                        style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
                      >
                        {item.productName} x{item.quantity}
                      </span>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </motion.section>

      {/* Floating Cart Bar */}
      <AnimatePresence>
        {cartItems.length > 0 && !checkoutOpen ? (
          <motion.button
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 16 }}
            type="button"
            onClick={openCheckout}
            className="fixed bottom-4 left-4 right-4 md:left-auto md:right-8 md:w-[380px] z-40 rounded-2xl border-4 border-[#2A2A45] bg-[#1C1C32] p-3 shadow-[6px_6px_0_0_#1a1a1a]"
          >
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 rounded-xl bg-[#F37020] border-3 border-[#2A2A45] text-white grid place-items-center shadow-[0_0_6px_rgba(243,112,32,0.1)]">
                  <ShoppingBag className="w-4 h-4" />
                </div>
                <div className="min-w-0 text-left">
                  <p className="text-sm font-black text-white truncate" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>{cartCount} item{cartCount === 1 ? "" : "s"} in bag</p>
                  <p className="text-xs text-[#6A6A8A] font-medium" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>Ready to check out</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm font-black text-white" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>{currency(cartSubtotal)}</p>
                <p className="text-xs text-[#F37020] font-bold" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>Open checkout</p>
              </div>
            </div>
          </motion.button>
        ) : null}
      </AnimatePresence>

      {/* Checkout Modal */}
      <AnimatePresence>
        {checkoutOpen ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/50 p-3 md:p-6"
            onClick={(e) => {
              if (e.target === e.currentTarget) closeCheckout()
            }}
          >
            <motion.div
              initial={{ opacity: 0, y: 20, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 14, scale: 0.97 }}
              transition={{ type: "spring", bounce: 0.2, duration: 0.4 }}
              className="mx-auto h-full max-w-5xl"
            >
              <div className="h-full md:h-auto md:max-h-[92vh] rounded-2xl border-4 border-[#2A2A45] bg-[#1C1C32] shadow-[8px_8px_0_0_#1a1a1a] overflow-hidden flex flex-col">
                {/* Checkout Header */}
                <div className="border-b-4 border-[#2A2A45] bg-gradient-to-r from-[#F37020]/10 via-white to-[#FFD700]/10 px-4 md:px-6 py-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-xs font-bold text-[#F37020]" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>Guided Checkout</p>
                      <h3
                        className="text-xl md:text-2xl font-black text-white"
                        style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
                      >
                        {checkoutStep === "success" ? "Order Complete!" : "Complete Your Order"}
                      </h3>
                      <p className="text-sm text-[#9A9ABF] mt-1 font-medium" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>
                        {checkoutStep === "cart" && "Step 1 of 3 - Review your selected items"}
                        {checkoutStep === "shipping" && "Step 2 of 3 - Shipping information"}
                        {checkoutStep === "review" && "Step 3 of 3 - Final review and mock payment"}
                        {checkoutStep === "success" && "Your mock checkout has been submitted successfully"}
                      </p>
                    </div>
                    <motion.button
                      type="button"
                      onClick={closeCheckout}
                      disabled={submitting}
                      whileHover={{ scale: 1.1, rotate: 90 }}
                      whileTap={{ scale: 0.9 }}
                      className="w-10 h-10 rounded-xl border-3 border-[#2A2A45] bg-[#1C1C32] text-[#9A9ABF] hover:text-white hover:bg-[#1A1A30] shadow-[0_0_6px_rgba(243,112,32,0.1)] transition-colors disabled:opacity-50 flex items-center justify-center"
                    >
                      <X className="w-4 h-4" />
                    </motion.button>
                  </div>

                  <div className="mt-4 flex flex-wrap gap-2">
                    <CheckoutStepPill label="Bag" active={checkoutStep === "cart"} done={stepIndex > 0} />
                    <CheckoutStepPill label="Shipping" active={checkoutStep === "shipping"} done={stepIndex > 1} />
                    <CheckoutStepPill label="Review" active={checkoutStep === "review"} done={stepIndex > 2} />
                    <CheckoutStepPill label="Complete" active={checkoutStep === "success"} done={checkoutStep === "success"} />
                  </div>
                </div>

                {/* Checkout Body */}
                <div className="flex-1 min-h-0 overflow-y-auto p-4 md:p-6 bg-[#1A1A30]">
                  <div className="grid grid-cols-1 lg:grid-cols-[1.3fr_0.8fr] gap-4 md:gap-6 items-start">
                    <div className="space-y-4">
                      {/* Cart Step */}
                      {checkoutStep === "cart" && (
                        <div className="rounded-2xl border-3 border-[#2A2A45] bg-[#1C1C32] p-4 shadow-[0_0_10px_rgba(243,112,32,0.15)]">
                          {cartItems.length === 0 ? (
                            <div className="py-8 text-center">
                              <ShoppingBag className="w-8 h-8 mx-auto text-[#6A6A8A]" />
                              <p className="mt-2 text-sm font-bold text-[#CCCCCC]" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>Your bag is empty</p>
                              <p className="text-xs text-[#6A6A8A] mt-1 font-medium" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>Close checkout and add items from the catalog.</p>
                            </div>
                          ) : (
                            <div className="space-y-3">
                              {cartItems.map(({ product, quantity }) => (
                                <div key={`checkout-${product._id}`} className="rounded-xl border-3 border-[#2A2A45] bg-[#1C1C32] p-3 shadow-[0_0_6px_rgba(243,112,32,0.1)]">
                                  <div className="flex items-start gap-3">
                                    <div className="w-16 h-16 rounded-xl overflow-hidden bg-[#1A1A30] border-2 border-[#2A2A45] flex-shrink-0">
                                      {product.primaryImageUrl ? (
                                        <img src={product.primaryImageUrl} alt={product.name} className="w-full h-full object-cover" />
                                      ) : (
                                        <div className="w-full h-full grid place-items-center text-[#6A6A8A]">
                                          <Package className="w-5 h-5" />
                                        </div>
                                      )}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-start justify-between gap-2">
                                        <div>
                                          <p className="text-sm font-black text-white truncate" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>{product.name}</p>
                                          <p className="text-xs text-[#6A6A8A] font-medium" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>{currency(product.priceUsd)} each</p>
                                        </div>
                                        <motion.button
                                          type="button"
                                          onClick={() => setCartQuantity(product, 0)}
                                          whileHover={{ scale: 1.1 }}
                                          whileTap={{ scale: 0.9 }}
                                          className="w-8 h-8 rounded-lg border-2 border-[#2A2A45] bg-[#1C1C32] text-[#6A6A8A] hover:text-red-600 hover:bg-red-50 shadow-none transition-colors flex items-center justify-center"
                                        >
                                          <Trash2 className="w-3.5 h-3.5" />
                                        </motion.button>
                                      </div>
                                      <div className="mt-2 flex items-center justify-between gap-2">
                                        <QtyControl
                                          compact
                                          value={quantity}
                                          onDecrease={() => setCartQuantity(product, quantity - 1)}
                                          onIncrease={() => setCartQuantity(product, quantity + 1)}
                                          canIncrease={quantity < Math.min(product.stock, product.maxPerOrder ?? product.stock)}
                                        />
                                        <div className="text-sm font-black text-white" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>{currency(product.priceUsd * quantity)}</div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Shipping Step */}
                      {checkoutStep === "shipping" && (
                        <div className="space-y-4">
                          {savedShipping ? (
                            <div className="rounded-2xl border-3 border-[#2A2A45] bg-[#FFD700]/10 p-4 shadow-[0_0_8px_rgba(243,112,32,0.12)]">
                              <div className="flex items-start justify-between gap-3 flex-wrap">
                                <div>
                                  <p className="text-sm font-black text-white" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>Saved shipping info</p>
                                  <p className="text-xs text-[#9A9ABF] mt-1 font-medium" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>Use your saved address to fill this step instantly.</p>
                                  <p className="text-xs text-[#6A6A8A] mt-2 font-medium" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>
                                    {savedShipping.fullName} - {savedShipping.city}, {savedShipping.state}
                                  </p>
                                </div>
                                <motion.button
                                  type="button"
                                  onClick={applySavedShipping}
                                  whileHover={{ scale: 1.03 }}
                                  whileTap={{ scale: 0.97 }}
                                  className="rounded-xl border-3 border-[#2A2A45] bg-[#FFD700] px-3 py-2 text-xs font-bold text-black shadow-[0_0_6px_rgba(243,112,32,0.1)]"
                                  style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
                                >
                                  Use saved info
                                </motion.button>
                              </div>
                            </div>
                          ) : null}

                          <div className="rounded-2xl border-3 border-[#2A2A45] bg-[#1C1C32] p-4 shadow-[0_0_10px_rgba(243,112,32,0.15)]">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              <Field label="Full Name" value={shipping.fullName} onChange={(v) => updateShippingField("fullName", v)} placeholder="Jane Doe" />
                              <Field label="Email" type="email" value={shipping.email} onChange={(v) => updateShippingField("email", v)} placeholder="you@company.com" />
                              <Field label="Phone" optional value={shipping.phone} onChange={(v) => updateShippingField("phone", v)} placeholder="(555) 555-5555" />
                              <div />
                              <div className="md:col-span-2">
                                <Field label="Address Line 1" value={shipping.addressLine1} onChange={(v) => updateShippingField("addressLine1", v)} placeholder="123 Market St" />
                              </div>
                              <div className="md:col-span-2">
                                <Field label="Address Line 2" optional value={shipping.addressLine2} onChange={(v) => updateShippingField("addressLine2", v)} placeholder="Apt, suite, unit" />
                              </div>
                              <Field label="City" value={shipping.city} onChange={(v) => updateShippingField("city", v)} placeholder="Miami" />
                              <Field label="State" value={shipping.state} onChange={(v) => updateShippingField("state", v)} placeholder="FL" />
                              <Field label="Postal Code" value={shipping.postalCode} onChange={(v) => updateShippingField("postalCode", v)} placeholder="33101" />
                              <Field label="Country" value={shipping.country} onChange={(v) => updateShippingField("country", v)} placeholder="United States" />
                              <div className="md:col-span-2">
                                <TextAreaField label="Order Notes" optional value={shipping.notes} onChange={(v) => updateShippingField("notes", v)} placeholder="Gift note, size preference, fulfillment note..." />
                              </div>
                            </div>
                            <label className="mt-3 inline-flex items-center gap-2 text-sm text-[#9A9ABF] font-medium select-none" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>
                              <input
                                type="checkbox"
                                checked={saveShippingForNextTime}
                                onChange={(e) => setSaveShippingForNextTime(e.target.checked)}
                                className="rounded border-[#2A2A45] border-2 text-[#F37020] focus:ring-[#F37020]"
                              />
                              Save shipping info for next time
                            </label>
                          </div>
                        </div>
                      )}

                      {/* Review Step */}
                      {checkoutStep === "review" && (
                        <div className="space-y-4">
                          <div className="rounded-2xl border-3 border-[#2A2A45] bg-[#1C1C32] p-4 shadow-[0_0_10px_rgba(243,112,32,0.15)]">
                            <h4 className="text-sm font-black text-white" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>Shipping</h4>
                            <div className="mt-2 text-sm text-[#9A9ABF] leading-6 font-medium" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>
                              <div className="font-bold text-white">{shipping.fullName}</div>
                              <div>{shipping.email}</div>
                              {shipping.phone ? <div>{shipping.phone}</div> : null}
                              <div className="mt-1">{shipping.addressLine1}</div>
                              {shipping.addressLine2 ? <div>{shipping.addressLine2}</div> : null}
                              <div>{shipping.city}, {shipping.state} {shipping.postalCode}</div>
                              <div>{shipping.country}</div>
                              {shipping.notes.trim() ? <div className="mt-2 text-xs text-[#6A6A8A]">Note: {shipping.notes.trim()}</div> : null}
                            </div>
                          </div>

                          <div className="rounded-2xl border-3 border-[#2A2A45] bg-[#1C1C32] p-4 shadow-[0_0_10px_rgba(243,112,32,0.15)]">
                            <h4 className="text-sm font-black text-white mb-3" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>Items</h4>
                            <div className="space-y-2">
                              {cartItems.map(({ product, quantity }) => (
                                <div key={`review-${product._id}`} className="flex items-center justify-between gap-3 text-sm">
                                  <div className="min-w-0">
                                    <div className="font-bold text-white truncate" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>{product.name}</div>
                                    <div className="text-xs text-[#6A6A8A] font-medium" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>{currency(product.priceUsd)} x {quantity}</div>
                                  </div>
                                  <div className="font-black text-white" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>{currency(product.priceUsd * quantity)}</div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Success Step */}
                      {checkoutStep === "success" && (
                        <div className="rounded-2xl border-4 border-[#2A2A45] bg-[#1C1C32] p-5 text-center shadow-[6px_6px_0_0_#1a1a1a]">
                          <motion.div
                            className="w-16 h-16 mx-auto rounded-2xl bg-[#32CD32] border-3 border-[#2A2A45] grid place-items-center text-white shadow-[0_0_8px_rgba(243,112,32,0.12)]"
                            initial={{ scale: 0 }}
                            animate={{ scale: 1, rotate: [0, 10, -10, 0] }}
                            transition={{ type: "spring", bounce: 0.5 }}
                          >
                            <CheckCircle2 className="w-8 h-8" />
                          </motion.div>
                          <h4 className="mt-4 text-2xl font-black text-white" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>Order Placed!</h4>
                          <p className="mt-2 text-sm text-[#9A9ABF] max-w-md mx-auto font-medium" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>
                            Your order was submitted and inventory was updated. Payment remains mocked for this phase.
                          </p>
                          {latestOrder ? (
                            <div className="mt-4 rounded-xl border-3 border-[#2A2A45] bg-[#1A1A30] p-4 text-left max-w-md mx-auto shadow-[0_0_8px_rgba(243,112,32,0.12)]">
                              <div className="flex items-start justify-between gap-3">
                                <div>
                                  <p className="text-sm font-black text-white" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>{latestOrder.orderNumber}</p>
                                  <p className="text-xs text-[#6A6A8A] font-medium" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>{formatOrderDate(latestOrder.createdAt)}</p>
                                </div>
                                <span className="rounded-lg border-2 border-[#2A2A45] bg-[#32CD32] px-2 py-1 text-[11px] text-white font-bold shadow-none" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>Mock Paid</span>
                              </div>
                              <div className="mt-3 text-sm text-[#CCCCCC] font-medium" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>Total: <span className="font-black text-white">{currency(latestOrder.totalUsd)}</span></div>
                            </div>
                          ) : null}
                        </div>
                      )}
                    </div>

                    {/* Order Summary Sidebar */}
                    <div className="space-y-4">
                      <div className="rounded-2xl border-3 border-[#2A2A45] bg-[#1C1C32] p-4 shadow-[0_0_10px_rgba(243,112,32,0.15)] lg:sticky lg:top-0">
                        <div className="flex items-center justify-between gap-2">
                          <h4 className="text-sm font-black text-white" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>Order Summary</h4>
                          <span className="text-xs font-bold text-[#6A6A8A] bg-[#1A1A30] border-2 border-[#2A2A45] rounded-lg px-2 py-0.5 shadow-none" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>{cartCount} items</span>
                        </div>
                        <div className="mt-3 space-y-2 max-h-[280px] overflow-auto pr-1">
                          {cartItems.length === 0 ? (
                            <p className="text-xs text-[#6A6A8A] font-medium" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>No items in bag.</p>
                          ) : (
                            cartItems.map(({ product, quantity }) => (
                              <div key={`summary-${product._id}`} className="flex items-center justify-between gap-2 text-xs">
                                <div className="min-w-0">
                                  <div className="font-bold text-white truncate" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>{product.name}</div>
                                  <div className="text-[#6A6A8A] font-medium" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>{currency(product.priceUsd)} x {quantity}</div>
                                </div>
                                <div className="font-black text-white" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>{currency(product.priceUsd * quantity)}</div>
                              </div>
                            ))
                          )}
                        </div>
                        <div className="mt-4 space-y-2 border-t-3 border-[#2A2A45] pt-3">
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-[#6A6A8A] font-medium" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>Subtotal</span>
                            <span className="font-bold text-white" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>{currency(cartSubtotal)}</span>
                          </div>
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-[#6A6A8A] font-medium" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>Payment</span>
                            <span className="font-bold text-[#32CD32]" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>Mock gateway</span>
                          </div>
                          <div className="flex items-center justify-between text-base pt-1">
                            <span className="text-white font-bold" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>Total</span>
                            <span className="text-white font-black tracking-tight" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>{currency(cartSubtotal)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Checkout Footer */}
                <div className="border-t-4 border-[#2A2A45] bg-[#1C1C32] px-4 md:px-6 py-4">
                  {checkoutMessage ? (
                    <div
                      className={[
                        "mb-3 rounded-xl border-3 border-[#2A2A45] px-3 py-2 text-sm font-bold shadow-[0_0_6px_rgba(243,112,32,0.1)]",
                        checkoutMessage.type === "success"
                          ? "bg-[#32CD32]/10 text-[#228B22]"
                          : "bg-red-100 text-red-700",
                      ].join(" ")}
                      style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
                    >
                      {checkoutMessage.text}
                    </div>
                  ) : null}

                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="text-xs text-[#6A6A8A] font-medium" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>
                      {checkoutStep !== "success" ? "Review your bag, enter shipping, and confirm your order." : "You can close this window and continue shopping."}
                    </div>

                    <div className="flex items-center gap-2">
                      {checkoutStep !== "cart" && checkoutStep !== "success" ? (
                        <motion.button
                          type="button"
                          onClick={goPrevStep}
                          whileHover={{ scale: 1.03 }}
                          whileTap={{ scale: 0.97 }}
                          className="inline-flex items-center gap-2 rounded-xl border-3 border-[#2A2A45] bg-[#1C1C32] px-4 py-2 text-sm font-bold text-[#CCCCCC] shadow-[0_0_8px_rgba(243,112,32,0.12)] transition-colors"
                          style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
                        >
                          <ArrowLeft className="w-4 h-4" />
                          Back
                        </motion.button>
                      ) : null}

                      {checkoutStep === "cart" || checkoutStep === "shipping" ? (
                        <motion.button
                          type="button"
                          onClick={goNextStep}
                          whileHover={{ scale: 1.03 }}
                          whileTap={{ scale: 0.97 }}
                          className="inline-flex items-center gap-2 rounded-xl border-3 border-[#2A2A45] bg-[#F37020] px-4 py-2 text-sm font-bold text-white shadow-[0_0_8px_rgba(243,112,32,0.12)] hover:bg-[#E06010] transition-colors"
                          style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
                        >
                          Continue
                          <ArrowRight className="w-4 h-4" />
                        </motion.button>
                      ) : null}

                      {checkoutStep === "review" ? (
                        <motion.button
                          type="button"
                          onClick={handleCheckout}
                          disabled={submitting}
                          whileHover={{ scale: 1.03 }}
                          whileTap={{ scale: 0.97 }}
                          className="inline-flex items-center gap-2 rounded-xl border-3 border-[#2A2A45] bg-[#32CD32] px-5 py-2 text-sm font-bold text-white shadow-[0_0_8px_rgba(243,112,32,0.12)] hover:bg-[#28B828] disabled:opacity-60 transition-colors"
                          style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
                        >
                          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
                          {submitting ? "Processing..." : "Complete Mock Checkout"}
                        </motion.button>
                      ) : null}

                      {checkoutStep === "success" ? (
                        <motion.button
                          type="button"
                          onClick={closeCheckout}
                          whileHover={{ scale: 1.03 }}
                          whileTap={{ scale: 0.97 }}
                          className="inline-flex items-center gap-2 rounded-xl border-3 border-[#2A2A45] bg-[#F37020] px-5 py-2 text-sm font-bold text-white shadow-[0_0_8px_rgba(243,112,32,0.12)] hover:bg-[#E06010] transition-colors"
                          style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
                        >
                          Done
                        </motion.button>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </motion.div>
  )
}
